import { useContext, useCallback, useState, useMemo } from "react";
import UIkit from "uikit";
import { AppContexts } from "../../providers";

const ALL_ACTIVITY_TYPES = [
  { key: "note", label: "Note", icon: "file-text" },
  { key: "phone_call", label: "Phone Call", icon: "receiver" },
  { key: "task", label: "Task", icon: "check" },
];

// Units/properties only allow note and task
const UNIT_ACTIVITY_TYPES = ["note", "task"];

const CRMActivityAdd = () => {
  const crmContext = useContext(AppContexts.CRMContext);
  const { activityTarget, activityTargetType } = crmContext;

  const [activityType, setActivityType] = useState("note");
  const [description, setDescription] = useState("");
  const [activityDate, setActivityDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );

  const isUnit = activityTargetType === "property";

  const availableTypes = useMemo(() => {
    if (isUnit) return ALL_ACTIVITY_TYPES.filter((t) => UNIT_ACTIVITY_TYPES.includes(t.key));
    return ALL_ACTIVITY_TYPES;
  }, [isUnit]);

  const resetForm = useCallback(() => {
    setActivityType("note");
    setDescription("");
    setActivityDate(new Date().toISOString().split("T")[0]);
  }, []);

  const handleSave = useCallback(async () => {
    if (!description.trim()) {
      UIkit.notification("Please enter a description.", "warning");
      return;
    }
    if (!activityTarget) return;

    const contactId = activityTarget.id || activityTarget.contact_id || activityTarget.rental_id;
    await crmContext.addActivity(contactId, {
      type: activityType,
      description: description.trim(),
      date: activityDate,
      target_type: activityTargetType,
      target_name: activityTarget.first_name
        ? `${activityTarget.first_name} ${activityTarget.last_name || ""}`.trim()
        : activityTarget.pms_id || "Unknown",
    });

    UIkit.notification("Activity added", "success");
    UIkit.offcanvas("#offcanvas-crm-activity").hide();
    resetForm();
  }, [activityTarget, activityTargetType, activityType, description, activityDate, crmContext, resetForm]);

  const targetLabel = activityTarget
    ? activityTarget.first_name
      ? `${activityTarget.first_name} ${activityTarget.last_name || ""}`.trim()
      : activityTarget.pms_id || ""
    : "";

  return (
    <div
      id="offcanvas-crm-activity"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
          ></button>
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">Add Activity</h2>
            {targetLabel && (
              <p className="crm-activity-target-label">
                For: <strong>{targetLabel}</strong>
                {activityTargetType && (
                  <span className={`crm-contact-badge ${activityTargetType}`}>
                    {activityTargetType.charAt(0).toUpperCase() + activityTargetType.slice(1)}
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="uk-modal-body">
            <div className="crm-activity-form">
              {/* Activity Type Selector */}
              <div className="crm-activity-type-selector">
                <div className="crm-activity-type-options uk-margin-small-top">
                  {availableTypes.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`crm-activity-type-btn ${activityType === t.key ? "is-active" : ""}`}
                      onClick={() => setActivityType(t.key)}
                    >
                      <span uk-icon={`icon: ${t.icon}; ratio: 0.9`}></span>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="uk-margin">
                <label className="uk-form-label">Description</label>
                <textarea
                  className="uk-textarea"
                  rows={4}
                  placeholder="Enter activity details…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* Date */}
              <div className="uk-margin">
                <label className="uk-form-label">Date</label>
                <input
                  className="uk-input"
                  type="date"
                  value={activityDate}
                  onChange={(e) => setActivityDate(e.target.value)}
                />
              </div>

              {/* Save / Cancel */}
              <div className="crm-activity-form-actions uk-margin-top">
                <button
                  type="button"
                  className="uk-button hms-btn hms-red-btn"
                  onClick={handleSave}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="uk-button hms-btn hms-white-btn"
                  onClick={() => {
                    UIkit.offcanvas("#offcanvas-crm-activity").hide();
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMActivityAdd;
